import type { OfferKind } from '@revelio/core/global-ai-assistant/offer-marker';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantEntityType,
} from './global-ai-assistant-api';

export type GlobalAiTranscriptMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'assistant';
      kind: 'text';
      text: string;
      /**
       * Progress narration streamed by a tool mid-run (see
       * global-ai-tool-progress.ts). Rendered like any other narration, but
       * EXCLUDED from the conversation history sent to the model so transient
       * "building your dashboard…" chatter never pollutes the next turn's
       * context or the summarizer.
       */
      ephemeral?: boolean;
      /**
       * Message ids of the offer messages this narration's inline
       * `[phrase](offer:<kind>)` triggers act on, stamped by the client tool
       * handler that appended the narration (it created/knows the backing
       * message). The drawer resolves each trigger by LOOKING UP this id —
       * never by transcript position — so a marker whose target is absent
       * renders as plain text, and a chip renders iff its id is unstamped.
       * kinds: nav → nav_confirm id; build → create_page_confirm id;
       * page → single_chart id (answerAsText or rendered); custom, filters →
       * the rendered single_chart id the follow-up acts on.
       */
      offerTargets?: Partial<Record<OfferKind, string>>;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'clarification';
      text: string;
      clarificationKind?: 'entity_value' | 'entity_scope';
      clarificationOptions?: GlobalAssistantEntityType[];
    }
  | { id: string; role: 'assistant'; kind: 'error'; text: string }
  | {
      id: string;
      role: 'assistant';
      kind: 'single_chart';
      intentSummary?: string;
      /** Factual line shown under the embedded chart (from intent \`chartSubtitle\`). */
      chartSubtitle?: string;
      item: PositionedItem;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      entityType: CustomDashAssistantEntityType;
      /**
       * Prompt that produced this chart. Used when opening a chart as a
       * custom page so follow-up filter context stays tied to this chart,
       * not whichever prompt happens to be latest.
       */
      prompt?: string;
      pipelineRequestId?: string;
      standardPagePath?: string;
      /**
       * Single-value answer: metadata-only message carrying the offer:page
       * wiring (standardPagePath + filters + prompt). The figure itself comes
       * from the headless digest service and is delivered as prose — this
       * message never mounts a chart (excluded from chart-preview liveness),
       * renders no card, and shows no "open as custom page" button.
       */
      answerAsText?: boolean;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'report_offers';
      offers: Array<{
        reportType: string;
        reportName: string;
        description?: string;
        sampleLink: string;
        confidence: number;
      }>;
      prompt?: string;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'nav_confirm';
      optionId: string;
      /** Display name of the destination (e.g. "Company Pay"). */
      pageName: string;
      /** Continuation seed: the user's task so a click can resume it. */
      continuationPrompt?: string;
      /** Set once the user has actioned this chip so it can't re-fire. */
      resolved?: boolean;
      /**
       * Resolved search_filters artifact backing a DATA-GROUNDED offer
       * (`offer_page_or_custom` with `filterArtifactId` — the causal
       * ground-then-offer flow). When present, a click applies THIS exact
       * artifact on the destination (same deterministic text=link contract
       * as `single_chart.filters`), so "engineer attrition is 26%…" lands on
       * a page filtered to engineers — never the page's default slice, and
       * never an independent re-resolution of the prompt.
       */
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      /** Entity anchor for `filters` (mirrors single_chart.entityType). */
      entityType?: GlobalAssistantEntityType;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'create_page_confirm';
      prompt: string;
      entityType: CustomDashAssistantEntityType;
      /** Set once the user has clicked Build so the chip can't re-fire. */
      resolved?: boolean;
      /**
       * PRE-GENERATED layout (resolve-first Phase C): `create_custom_page` ran
       * the page-gen stream headlessly during the turn; the click seeds a new
       * draft with this exact layout and navigates — no empty draft, no
       * on-page build, no resumed turn. Absent → legacy confirm-navigate-
       * resume flow (also the automatic fallback when generation failed or
       * asked clarification questions).
       */
      generated?: {
        content: LLMTabContentForDispatch;
        filters?: TalentDiscoveryAiFilterSearchResponseV2;
      };
      /**
       * Resolved search_filters artifact backing a DATA-GROUNDED offer — the
       * SAME artifact `offer_page_or_custom` stored on its nav chip. Used as
       * the landing-filter FALLBACK when the lazy page-gen's own filter
       * resolution comes back empty/unresolved (e.g. its search could not
       * match the named company), so the built draft still lands scoped to
       * the entity the offer named instead of the page default.
       */
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'reasoning_trace';
      trace: import('./reasoning-trace').ReasoningTrace;
    };

export type GlobalAiInlineChartSpec = {
  messageId: string;
  item: PositionedItem;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  entityType: CustomDashAssistantEntityType;
  chartSubtitle?: string;
};

export type GlobalAiSingleChartTranscriptMessage = Extract<
  GlobalAiTranscriptMessage,
  { kind: 'single_chart' }
>;

export function toGlobalAiInlineChartSpec(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAiInlineChartSpec {
  return {
    messageId: msg.id,
    item: msg.item,
    filters: msg.filters,
    entityType: msg.entityType,
    chartSubtitle: msg.chartSubtitle,
  };
}
